import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { downloadAuthenticatedFile } from "@/lib/download";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldAlert, Users, TrendingDown, TrendingUp, ArrowRight, AlertTriangle,
  LogOut, UserPlus, Bell, FileDown, Timer, X, CheckCircle2,
} from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import CrossSpecialtyAlerts from "@/components/CrossSpecialtyAlerts";

const RISK_STYLES = {
  high:   { color: "#F87171", bg: "rgba(248,113,113,0.12)", label: "HIGH" },
  medium: { color: "#FBBF24", bg: "rgba(251,191,36,0.12)", label: "MEDIUM" },
  low:    { color: "#4ADE80", bg: "rgba(74,222,128,0.12)", label: "LOW" },
};

const STATUS_STYLES = {
  ok:        { color: "#4ADE80", bg: "rgba(74,222,128,0.10)" },
  attention: { color: "#FBBF24", bg: "rgba(251,191,36,0.12)" },
  warning:   { color: "#FB923C", bg: "rgba(251,146,60,0.12)" },
  critical:  { color: "#F87171", bg: "rgba(248,113,113,0.14)" },
};

const SPECIALTY_META = {
  nephrology: { label: "Nephrology inbox",  accent: "#4ADE80", tagline: "Triage sorted by KFRE and eGFR decline" },
  cardiology: { label: "Cardiology inbox",  accent: "#F87171", tagline: "Sorted by BP status, hypertensive crises first" },
  neurology:  { label: "Neurology inbox",   accent: "#A78BFA", tagline: "Stroke severity, seizure frequency and migraine load surfaced first" },
  oncology:   { label: "Oncology inbox",    accent: "#FBBF24", tagline: "RECIST progression and chemo cycle status at the top" },
};

const SEV_STYLES = {
  critical: { color: "#F87171", bg: "rgba(248,113,113,0.12)", label: "CRITICAL" },
  high:     { color: "#FBBF24", bg: "rgba(251,191,36,0.12)", label: "HIGH" },
};

export default function ProviderInbox() {
  const { setViewingPatient, user, logout } = useAuth();
  const [patients, setPatients] = useState([]);
  const [providerSpecialty, setProviderSpecialty] = useState(
    () => user?.specialty || "nephrology"
  );
  const [alerts, setAlerts] = useState([]);
  const [rpm, setRpm] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    const [pRes, aRes, rRes] = await Promise.all([
      api.get("/provider/patients"),
      api.get("/provider/alerts"),
      api.get("/provider/rpm/report"),
    ]);
    // Backend now returns { provider_specialty, patients }; keep support for
    // the legacy Array shape in case a proxy caches an old response.
    const payload = pRes.data;
    if (Array.isArray(payload)) {
      setPatients(payload);
      setProviderSpecialty(user?.specialty || "nephrology");
    } else {
      setPatients(payload.patients || []);
      setProviderSpecialty(payload.provider_specialty || user?.specialty || "nephrology");
    }
    setAlerts(aRes.data);
    setRpm(rRes.data);
  };

  useEffect(() => {
    setViewingPatient(null);
    (async () => {
      try { await load(); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPatient = (patient) => {
    setViewingPatient(patient);
    navigate("/");
  };

  const ackAlert = async (id) => {
    await api.post(`/provider/alerts/${id}/acknowledge`);
    await load();
  };

  const patientRpmMinutes = (pid) => {
    const row = rpm?.rows?.find((r) => r.patient_id === pid);
    return row ? row.minutes : 0;
  };

  const highRisk = patients.filter((p) => p.overall_risk === "high").length;
  const mediumRisk = patients.filter((p) => p.overall_risk === "medium").length;
  const total = patients.length;

  return (
    <div className="min-h-screen bg-[#080A09] text-white" data-testid="provider-inbox-page">
      <div className="border-b border-[#272E2B]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-2xl bg-[#4ADE80] flex items-center justify-center ros-glow-primary shrink-0">
            <span className="text-black font-heading font-bold text-lg">R</span>
          </div>
          <div>
            <div className="ros-label" style={{ color: SPECIALTY_META[providerSpecialty]?.accent }}
                 data-testid="provider-inbox-specialty-label">
              {SPECIALTY_META[providerSpecialty]?.label || "Provider portal"}
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl mt-1 tracking-tight">Patient inbox</h1>
            <p className="text-sm text-[#A3ACA7] mt-1">
              {user?.name} · {SPECIALTY_META[providerSpecialty]?.tagline || "triage view sorted by clinical risk"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-6 pr-3">
            <div className="text-right">
              <div className="ros-label" style={{ fontSize: 10 }}>Roster</div>
              <div className="font-heading text-2xl">{total}</div>
            </div>
            <div className="text-right">
              <div className="ros-label" style={{ fontSize: 10, color: "#F87171" }}>High risk</div>
              <div className="font-heading text-2xl text-[#F87171]">{highRisk}</div>
            </div>
            <div className="text-right">
              <div className="ros-label" style={{ fontSize: 10, color: "#FBBF24" }}>Medium</div>
              <div className="font-heading text-2xl text-[#FBBF24]">{mediumRisk}</div>
            </div>
          </div>
          <button
            onClick={() => window.dispatchEvent(new Event("open-command-palette"))}
            data-testid="cmdk-open-btn"
            className="hidden md:flex items-center gap-2 px-3 py-2 rounded-full text-xs border border-[#272E2B] text-[#A3ACA7] hover:bg-[#1C201E] hover:text-white transition-colors"
            title="Search everything (⌘K)"
          >
            <span>Search</span>
            <span className="px-1.5 py-0.5 rounded border border-[#272E2B] font-mono">⌘K</span>
          </button>
          <button
            onClick={() => {
              const now = new Date();
              const label = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
              downloadAuthenticatedFile("/provider/rpm/report.pdf", `rpm-${label}.pdf`);
            }}
            data-testid="rpm-pdf-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm border border-[#272E2B] text-[#A3ACA7] hover:bg-[#1C201E] hover:text-white transition-colors"
          >
            <FileDown size={14} /> RPM PDF
          </button>
          <button
            onClick={() => navigate("/provider/add-patient")}
            data-testid="add-patient-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90 font-semibold transition-colors"
          >
            <UserPlus size={14} /> Add patient
          </button>
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            data-testid="logout-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm border border-[#272E2B] text-[#A3ACA7] hover:bg-[#1C201E] hover:text-white transition-colors"
          >
            <LogOut size={14} /> Log out
          </button>
          <ThemeToggle />
        </div>
        </div>
      </div>

      {/* Cross-Specialty AI review-needed panel — critical/high insights across the whole roster */}
      <CrossSpecialtyAlerts onOpenPatient={openPatient} patients={patients} />

      {/* RPM Progress bar */}
      {rpm && (
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-6" data-testid="rpm-progress-summary">
          <div className="ros-card-raised">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Timer size={16} className="text-[#4ADE80]" />
                <div className="ros-label">RPM billable minutes · {rpm.month}</div>
              </div>
              <div className="text-xs text-[#A3ACA7]">
                <span className="text-white font-semibold" data-testid="rpm-total-min">{rpm.totals.total_minutes}</span> mins
                across {rpm.totals.patients} patients · <span className="text-[#4ADE80] font-semibold">{rpm.totals.billable}</span> billable at CPT 99457
              </div>
            </div>
            {/* Global summary as multi-segment bar per patient */}
            <div className="flex gap-1 h-2">
              {rpm.rows.slice(0, 20).map((r) => {
                const pct = Math.min(r.minutes / 20 * 100, 100);
                const billable = r.billable_99457;
                return (
                  <div key={r.patient_id} className="flex-1 bg-[#1C201E] rounded overflow-hidden"
                       title={`${r.name}: ${r.minutes} min`}>
                    <div className="h-full transition-all"
                         style={{ width: `${pct}%`, background: billable ? "#4ADE80" : "#FBBF24" }} />
                  </div>
                );
              })}
              {rpm.rows.length === 0 && (
                <div className="flex-1 text-center text-xs text-[#A3ACA7]">
                  No review time logged this month yet — open a patient to start the clock
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Urgent review alerts */}
      {alerts.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-6" data-testid="urgent-alerts-section">
          <div className="ros-card border-l-2 border-l-[#F87171]">
            <div className="flex items-center gap-2 mb-3">
              <Bell size={16} className="text-[#F87171]" />
              <div className="ros-label" style={{ color: "#F87171" }}>Urgent review · {alerts.length} open</div>
            </div>
            <div className="space-y-2">
              {alerts.map((a) => {
                const s = SEV_STYLES[a.severity] || SEV_STYLES.high;
                const patient = patients.find((p) => p.patient_id === a.patient_id);
                return (
                  <div key={a.id} className="ros-card-raised flex items-center justify-between gap-4"
                       data-testid={`alert-${a.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0"
                            style={{ color: s.color, background: s.bg }}>
                        {s.label}
                      </span>
                      <button
                        onClick={() => patient && openPatient(patient)}
                        className="font-semibold text-white hover:text-[#4ADE80] transition-colors shrink-0"
                      >
                        {a.patient_name}
                      </button>
                      <span className="text-sm text-[#A3ACA7] truncate">{a.message}</span>
                    </div>
                    <button
                      onClick={() => ackAlert(a.id)}
                      data-testid={`ack-alert-${a.id}`}
                      className="text-[#A3ACA7] hover:text-[#4ADE80] p-1 shrink-0"
                      title="Acknowledge alert"
                    >
                      <CheckCircle2 size={16} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="p-6 md:p-10">
        {loading && <div className="text-center text-[#A3ACA7] py-16">Loading patient roster…</div>}
        {!loading && patients.length === 0 && (
          <div className="ros-card text-center py-16">
            <Users size={40} className="mx-auto text-[#A3ACA7] mb-4" />
            <div className="font-heading text-xl">No patients linked yet</div>
            <div className="text-sm text-[#A3ACA7] mt-2">
              Add patients to your roster to see them here.
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="patient-grid">
          {patients.map((p, idx) => {
            const rs = RISK_STYLES[p.overall_risk];
            const mins = patientRpmMinutes(p.patient_id);
            const pct = Math.min(mins / 20 * 100, 100);
            return (
              <motion.button
                key={p.patient_id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                onClick={() => openPatient(p)}
                data-testid={`patient-card-${p.email}`}
                className="text-left ros-card hover:border-[#4ADE80]/40 transition-colors group"
                style={{ borderLeftColor: rs.color, borderLeftWidth: 3 }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-heading text-xl">{p.name}</div>
                      <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full"
                            style={{ color: rs.color, background: rs.bg }}>
                        {rs.label}
                      </span>
                    </div>
                    <div className="text-xs text-[#A3ACA7] mt-1">
                      {p.age} y · {p.sex} · CKD {p.ckd_stage || "—"} · {p.medication_count} meds
                    </div>
                  </div>
                  <ArrowRight size={18}
                    className="text-[#A3ACA7] group-hover:text-[#4ADE80] group-hover:translate-x-0.5 transition-all" />
                </div>

                <div className="grid grid-cols-3 gap-3 mt-5" data-testid={`patient-tiles-${p.email}`}>
                  <SpecialtyTiles specialty={providerSpecialty} p={p} />
                </div>

                {/* Per-patient RPM progress */}
                <div className="mt-4" data-testid={`rpm-progress-${p.email}`}>
                  <div className="flex items-center justify-between text-[10px] text-[#A3ACA7]">
                    <span className="uppercase tracking-wider font-semibold">RPM this month</span>
                    <span className={mins >= 20 ? "text-[#4ADE80] font-semibold" : ""}>
                      {mins} / 20 mins {mins >= 20 ? "· 99457 billable" : ""}
                    </span>
                  </div>
                  <div className="mt-1.5 h-1.5 rounded bg-[#1C201E] overflow-hidden">
                    <div className="h-full transition-all"
                         style={{ width: `${pct}%`, background: mins >= 20 ? "#4ADE80" : "#FBBF24" }} />
                  </div>
                </div>

                {p.risk_flags.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {p.risk_flags.map((f, i) => {
                      const s = RISK_STYLES[f.level];
                      return (
                        <span key={i}
                              className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-md"
                              style={{ color: s.color, background: s.bg, border: `1px solid ${s.color}33` }}>
                          {f.level === "high" ? <ShieldAlert size={11} /> : <AlertTriangle size={11} />}
                          {f.label}
                        </span>
                      );
                    })}
                  </div>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 * SpecialtyTiles — swaps the 3-tile KPI row inside every patient
 * card based on the logged-in provider's specialty. Cardiologist
 * lands on BP-first, oncologist on RECIST-first, and so on.
 * ============================================================ */
function SpecialtyTiles({ specialty, p }) {
  if (specialty === "cardiology") {
    const c = p.specialty_metrics?.cardiology;
    const s = c ? STATUS_STYLES[c.status] || STATUS_STYLES.ok : null;
    return (
      <>
        <div className="ros-card-raised" data-testid={`tile-bp-${p.email}`}
             style={s ? { borderColor: `${s.color}55` } : {}}>
          <div className="ros-label" style={{ fontSize: 9 }}>Blood pressure</div>
          <div className="ros-metric text-2xl mt-1"
               style={{ color: s?.color || "#fff" }}>
            {c?.systolic != null ? `${c.systolic}/${c.diastolic}` : "—"}
          </div>
          <div className="text-[10px] text-[#A3ACA7] mt-1 truncate">
            {c?.bp_category || "no readings"}
          </div>
        </div>
        <div className="ros-card-raised" data-testid={`tile-ldl-${p.email}`}>
          <div className="ros-label" style={{ fontSize: 9 }}>LDL</div>
          <div className="ros-metric text-2xl mt-1"
               style={{ color: c?.ldl_high ? "#F87171" : "#fff" }}>
            {c?.latest_ldl != null ? Math.round(c.latest_ldl) : "—"}
          </div>
          <div className="text-[10px] text-[#A3ACA7] mt-1">mg/dL</div>
        </div>
        <div className="ros-card-raised" data-testid={`tile-egfr-${p.email}`}>
          <div className="ros-label" style={{ fontSize: 9 }}>eGFR</div>
          <div className="ros-metric text-2xl mt-1">
            {p.latest_egfr != null ? p.latest_egfr.toFixed(0) : "—"}
          </div>
          <div className="text-[10px] text-[#A3ACA7] mt-1">baseline</div>
        </div>
      </>
    );
  }

  if (specialty === "neurology") {
    const n = p.specialty_metrics?.neurology;
    const s = n ? STATUS_STYLES[n.status] || STATUS_STYLES.ok : null;
    return (
      <>
        <div className="ros-card-raised" data-testid={`tile-neuro-primary-${p.email}`}
             style={s ? { borderColor: `${s.color}55` } : {}}>
          <div className="ros-label" style={{ fontSize: 9 }}>
            {n?.primary_metric || "Neurology"}
          </div>
          <div className="ros-metric text-2xl mt-1"
               style={{ color: s?.color || "#fff" }}>
            {n?.value != null ? n.value : "—"}
          </div>
          <div className="text-[10px] text-[#A3ACA7] mt-1 truncate">
            {n?.secondary || "no data"}
          </div>
        </div>
        <div className="ros-card-raised" data-testid={`tile-bp-${p.email}`}>
          <div className="ros-label" style={{ fontSize: 9 }}>BP</div>
          <div className="ros-metric text-2xl mt-1">
            {p.specialty_metrics?.cardiology?.systolic != null
              ? `${p.specialty_metrics.cardiology.systolic}/${p.specialty_metrics.cardiology.diastolic}`
              : "—"}
          </div>
          <div className="text-[10px] text-[#A3ACA7] mt-1">mmHg</div>
        </div>
        <div className="ros-card-raised" data-testid={`tile-egfr-${p.email}`}>
          <div className="ros-label" style={{ fontSize: 9 }}>eGFR</div>
          <div className="ros-metric text-2xl mt-1">
            {p.latest_egfr != null ? p.latest_egfr.toFixed(0) : "—"}
          </div>
          <div className="text-[10px] text-[#A3ACA7] mt-1">baseline</div>
        </div>
      </>
    );
  }

  if (specialty === "oncology") {
    const o = p.specialty_metrics?.oncology;
    const s = o ? STATUS_STYLES[o.status] || STATUS_STYLES.ok : null;
    return (
      <>
        <div className="ros-card-raised" data-testid={`tile-recist-${p.email}`}
             style={s ? { borderColor: `${s.color}55` } : {}}>
          <div className="ros-label" style={{ fontSize: 9 }}>RECIST</div>
          <div className="ros-metric text-2xl mt-1"
               style={{ color: s?.color || "#fff" }}>
            {o?.response || "—"}
          </div>
          <div className="text-[10px] text-[#A3ACA7] mt-1 truncate">
            {o?.stage ? `Stage ${o.stage}` : "no dx"}
            {o?.change_from_baseline_pct != null && ` · ${o.change_from_baseline_pct > 0 ? "+" : ""}${o.change_from_baseline_pct}%`}
          </div>
        </div>
        <div className="ros-card-raised" data-testid={`tile-cycle-${p.email}`}>
          <div className="ros-label" style={{ fontSize: 9 }}>Chemo cycle</div>
          <div className="ros-metric text-2xl mt-1">
            {o?.cycle_number != null ? `C${o.cycle_number}/${o.total_cycles}` : "—"}
          </div>
          <div className="text-[10px] text-[#A3ACA7] mt-1 truncate">
            {o?.regimen || "no regimen"}
          </div>
          {o?.cycle_progress_pct != null && (
            <div className="mt-1 h-1 rounded bg-[#1C201E] overflow-hidden">
              <div className="h-full" style={{
                width: `${Math.min(100, o.cycle_progress_pct)}%`, background: "#FBBF24"
              }} />
            </div>
          )}
        </div>
        <div className="ros-card-raised" data-testid={`tile-ecog-${p.email}`}>
          <div className="ros-label" style={{ fontSize: 9 }}>ECOG</div>
          <div className="ros-metric text-2xl mt-1">
            {o?.ecog != null ? o.ecog : "—"}
          </div>
          <div className="text-[10px] text-[#A3ACA7] mt-1">performance</div>
        </div>
      </>
    );
  }

  // Nephrology (default) — legacy tiles
  const trendDown = p.egfr_trend != null && p.egfr_trend < 0;
  return (
    <>
      <div className="ros-card-raised" data-testid={`tile-egfr-${p.email}`}>
        <div className="ros-label" style={{ fontSize: 9 }}>eGFR</div>
        <div className="flex items-baseline gap-1 mt-1">
          <div className="ros-metric text-2xl">
            {p.latest_egfr != null ? p.latest_egfr.toFixed(1) : "—"}
          </div>
          {p.egfr_trend != null && (
            trendDown ? <TrendingDown size={14} className="text-[#F87171]" />
                      : <TrendingUp size={14} className="text-[#4ADE80]" />
          )}
        </div>
        <div className="text-[10px] text-[#A3ACA7] mt-1">mL/min/1.73m²</div>
      </div>
      <div className="ros-card-raised" data-testid={`tile-acr-${p.email}`}>
        <div className="ros-label" style={{ fontSize: 9 }}>ACR</div>
        <div className="ros-metric text-2xl mt-1">
          {p.latest_acr != null ? Math.round(p.latest_acr) : "—"}
        </div>
        <div className="text-[10px] text-[#A3ACA7] mt-1">mg/g</div>
      </div>
      <div className="ros-card-raised" data-testid={`tile-kfre-${p.email}`}>
        <div className="ros-label" style={{ fontSize: 9 }}>KFRE 5-yr</div>
        <div className="ros-metric text-2xl mt-1"
             style={{ color: (p.kfre_5yr || 0) > 10 ? "#F87171" : (p.kfre_5yr || 0) > 5 ? "#FBBF24" : "#fff" }}>
          {p.kfre_5yr != null ? `${p.kfre_5yr.toFixed(1)}%` : "—"}
        </div>
        <div className="text-[10px] text-[#A3ACA7] mt-1">dialysis risk</div>
      </div>
    </>
  );
}
